import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import { X, Trash2, RotateCcw, User, Clock, Search } from 'lucide-react'
import BaseModal from '../BaseModal'
import AuditApiService, { AuditLogEntry } from '../../services/AuditApiService'
import ConfirmationModal from '../ModalConfirmation'
import { FeedbackContext } from '../../contexts/FeedbackContext'


const ModalCard = styled.div`
  background: #ffffff;
  width: 90vw;
  max-width: 900px;
  max-height: 85vh;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: 'Inter', sans-serif;
`

const ModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #eaeaea;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fafafa;
`

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
`

const CloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666666;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f0f0f0;
    color: #1a1a1a;
  }
`

const ModalBody = styled.div`
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background: #ffffff;

  input {
    border: none;
    outline: none;
    width: 100%;
    font-size: 14px;
    font-family: 'Inter', sans-serif;
  }
`

const RecordsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th,
  td {
    border-bottom: 1px solid #f0f0f0;
    padding: 12px 16px;
    text-align: left;
  }

  th {
    background: #f9fafb;
    color: #6b7280;
    font-weight: 600;
  }

  tbody tr:hover {
    background: #fbfbfb;
  }
`

const RestoreButton = styled.button<{ $restoring?: boolean }>`
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s ease;
  opacity: ${({ $restoring }) => ($restoring ? 0.6 : 1)};

  &:hover {
    background: #1d4ed8;
  }
`

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  font-size: 13px;
  color: #6b7280;
`

const PageButton = styled.button`
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 13px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: #f3f4f6;
  }
`

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #8c8c8c;
  font-size: 14px;
`

interface DeletedRecordsModalProps {
  tableName?: string
  moduleName?: string
  onClose: () => void
  onRecordRestored?: () => void
}

export const DeletedRecordsModal: React.FC<DeletedRecordsModalProps> = ({
  tableName,
  moduleName,
  onClose,
  onRecordRestored,
}) => {
  const [records, setRecords] = useState<AuditLogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [search, setSearch] = useState('')
  const [restoringId, setRestoringId] = useState<number | null>(null)
  const [recordToRestore, setRecordToRestore] = useState<AuditLogEntry | null>(null)
  const { setFeedback } = useContext(FeedbackContext)

  const loadDeletedRecords = async (targetPage = 1) => {
    try {
      setLoading(true)
      const data = await AuditApiService.getDeletedRecords(tableName, targetPage, 10)
      setRecords(data.records || [])
      setTotalPages(data.totalPages || 1)
      setPage(data.page || 1)
    } catch (error) {
      console.error('Erro ao carregar registros excluídos:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadDeletedRecords(1)
  }, [tableName])

  const handleConfirmRestore = async () => {
    if (!recordToRestore) return
    const id = recordToRestore.id
    try {
      setRestoringId(id)
      await AuditApiService.restoreDeletedRecord(id)
      setFeedback({ message: 'Registro restaurado com sucesso!', isOpen: true, statusOk: true })
      setRecordToRestore(null)
      await loadDeletedRecords(page)
      onRecordRestored?.()
    } catch (error: any) {
      console.error('Erro ao restaurar registro:', error)
      const errorMsg = error?.response?.data?.message || 'Não foi possível restaurar o registro.'
      setFeedback({ message: errorMsg, isOpen: true, statusOk: false })
      setRecordToRestore(null)
    } finally {
      setRestoringId(null)
    }
  }

  const filteredRecords = records.filter((rec) => {
    if (!search) return true
    const searchLower = search.toLowerCase()
    const nameMatch = JSON.stringify(rec.oldValue || {}).toLowerCase().includes(searchLower)
    const userMatch = rec.ChangedBy?.name?.toLowerCase().includes(searchLower)
    const idMatch = String(rec.recordId).includes(searchLower)
    return nameMatch || userMatch || idMatch
  })

  return (
    <BaseModal>
      <ModalCard>
        <ModalHeader>
          <TitleGroup>
            <Trash2 size={20} color="#ef4444" />
            <div>
              <HeaderTitle>Registros Excluídos (Auditoria)</HeaderTitle>
              <div style={{ fontSize: 12, color: '#737373', marginTop: 2 }}>
                {moduleName || tableName ? `Módulo: ${moduleName || tableName}` : 'Todos os módulos'}
              </div>
            </div>
          </TitleGroup>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <SearchBar>
            <Search size={18} color="#9ca3af" />
            <input
              type="text"
              placeholder="Buscar por ID, nome ou usuário..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchBar>

          {loading ? (
            <EmptyState>Carregando registros excluídos...</EmptyState>
          ) : filteredRecords.length === 0 ? (
            <EmptyState>Nenhum registro excluído encontrado.</EmptyState>
          ) : (
            <RecordsTable>
              <thead>
                <tr>
                  <th style={{ width: '80px' }}>ID</th>
                  <th>Registro</th>
                  <th>Excluído Por</th>
                  <th>Data e Hora</th>
                  <th style={{ textAlign: 'right' }}>Ação</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map((record) => {
                  const data = record.oldValue || {}
                  const displayName =
                    data.name ||
                    data.companyName ||
                    data.plate ||
                    data.code ||
                    data.title ||
                    `Registro #${record.recordId}`

                  return (
                    <tr key={record.id}>
                      <td style={{ fontWeight: 600, color: '#4b5563' }}>
                        #{record.recordId}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, color: '#111827' }}>
                          {displayName}
                        </div>
                        {data.description && (
                          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                            {data.description}
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <User size={14} color="#6b7280" />
                          <span>{record.ChangedBy?.name || 'Sistema'}</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Clock size={14} color="#6b7280" />
                          <span>{new Date(record.changedAt).toLocaleString('pt-BR')}</span>
                        </div>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <RestoreButton
                          onClick={() => setRecordToRestore(record)}
                          disabled={restoringId === record.id}
                          $restoring={restoringId === record.id}
                        >
                          <RotateCcw size={14} />
                          {restoringId === record.id ? 'Restaurando...' : 'Restaurar'}
                        </RestoreButton>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </RecordsTable>
          )}

          {totalPages > 1 && (
            <PaginationContainer>
              <div>
                Página {page} de {totalPages}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <PageButton
                  disabled={page <= 1}
                  onClick={() => loadDeletedRecords(page - 1)}
                >
                  Anterior
                </PageButton>
                <PageButton
                  disabled={page >= totalPages}
                  onClick={() => loadDeletedRecords(page + 1)}
                >
                  Próxima
                </PageButton>
              </div>
            </PaginationContainer>
          )}
        </ModalBody>
      </ModalCard>

      <ConfirmationModal
        isOpen={!!recordToRestore}
        title="Confirmar Restauração"
        message={
          <div>
            <p>Tem certeza de que deseja restaurar este registro excluído?</p>
            {recordToRestore && (
              <p style={{ marginTop: 8, fontSize: 13, color: '#6b7280' }}>
                Registro: #{recordToRestore.recordId} - {recordToRestore.oldValue?.name || recordToRestore.oldValue?.companyName || recordToRestore.oldValue?.plate || recordToRestore.oldValue?.code || recordToRestore.tableName}
              </p>
            )}
          </div>
        }
        actions={[
          { label: 'Cancelar', onClick: () => setRecordToRestore(null), variant: 'outline' },
          { label: 'Restaurar', onClick: handleConfirmRestore, variant: 'primary' },
        ]}
      />
    </BaseModal>
  )
}

export default DeletedRecordsModal
